import React, { useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import * as Notify from "@repoflow/form-schema/notify";
import * as Loading from "@repoflow/components/loading";

// "Connect an agent": mint an `agent` key for somebody's AI agent, show the
// secret once with how to connect it to the service's MCP server, and list and
// revoke the agent keys. docs/scoped-keys.md §Follow-ups (P278).
//
// The panel is the same in every service; what each one supplies is what the
// keys are bound to and how it asks for them:
//
//   service   the MCP server's name, as the Claude Code line registers it
//   scope     `{ service, type, id }` — the resource the keys are bound to,
//             which the app's own route or picker chose (pagews: the site in
//             the URL). It is sent with the mint as `roles: ["agent"]` and
//             `scope`, the one shape auth-repoflow accepts for a bound key.
//             null when the service's API binds the key itself and takes only
//             who it is for (noshchat's `keys.createAgent`).
//   queries   `{ agents, mint, remove }` — the app's own documents. `agents`
//             answers `viewer.account.keys.agents { complete items }`; `mint`
//             answers the key, keySecret included, at
//             `viewer.account.keys.<mintField>`; `remove` takes `$keyId`.
//   variables the `agents` query's variables (pagews: `{ siteId }`)
//   update    an optional cache update after a mint or a revoke, for other
//             screens' key listings; the agents listing is refetched anyway
//   children  what an agent key may do here, in the service's own words
//   server    how the connect line names the MCP server ("this site's MCP
//             server"); `<service>'s MCP server` by default
//   keysScreen where every key is listed, for a listing that may be partial
//
// The API is served from the app's own host (CloudFront's `/api/*`), so the
// MCP endpoint is this origin's.

export const mcpUrlOf = (origin) => `${origin}/api/mcp`;

export const connectionOf = (service, origin, keyId, keySecret) => {
  const url = mcpUrlOf(origin);
  const header = `Authorization: Bearer ${keyId}/${keySecret}`;
  return {
    url,
    header,
    claudeCode: `claude mcp add --transport http ${service} ${url} --header "${header}"`,
  };
};

/** The mint's input: who it is for, and the binding when the panel sends one. */
export const mintInputOf = (scope, description) => {
  const trimmed = (description || "").trim();
  return {
    ...(scope ? { roles: ["agent"], scope } : {}),
    ...(trimmed ? { description: trimmed } : {}),
  };
};

const origin = () => window.location.origin;

export const Panel = ({
  service,
  scope = null,
  queries,
  variables = {},
  mintField = "create",
  update,
  title,
  placeholder = "e.g. Claude Code on my laptop",
  empty = "No agent keys yet.",
  server = `${service}'s MCP server`,
  keysScreen = "Keys",
  children,
}) => {
  const [minted, setMinted] = useState(null);
  const refetch = { refetchQueries: [{ query: queries.agents, variables }], awaitRefetchQueries: true };

  return (
    <>
      <Card className="mt-3">
        <CardBody>
          {children}
          <p className="mb-1">The agent connects to {server}:</p>
          <p className="mb-3">
            <code>{mcpUrlOf(origin())}</code> with the header{" "}
            <code>Authorization: Bearer &lt;keyId&gt;/&lt;keySecret&gt;</code>
          </p>
          <Mint
            scope={scope}
            mutation={queries.mint}
            mintField={mintField}
            refetch={refetch}
            update={update}
            placeholder={placeholder}
            onMinted={setMinted}
          />
        </CardBody>
      </Card>

      <SecretModal service={service} minted={minted} onClose={() => setMinted(null)} />

      {title && <h5 className="mt-4">{title}</h5>}
      <Listing
        queries={queries}
        variables={variables}
        refetch={refetch}
        update={update}
        empty={empty}
        keysScreen={keysScreen}
      />
    </>
  );
};

const Mint = ({ scope, mutation, mintField, refetch, update, placeholder, onMinted }) => {
  const [description, setDescription] = useState("");
  const [mint, { loading }] = useMutation(mutation, {
    ...refetch,
    ...(update ? { update: update({}) } : {}),
    onCompleted: (data) => {
      const key = data.viewer.account.keys[mintField];
      setDescription("");
      onMinted({ keyId: key.keyId, keySecret: key.keySecret });
    },
    onError: (error) => Notify.error(String(error)),
  });

  const submit = () => mint({ variables: { input: mintInputOf(scope, description) } });

  return (
    <Row className="align-items-end">
      <Col md={7}>
        <FormGroup className="mb-md-0">
          <Label for="agent-key-description">Who is it for?</Label>
          <Input
            id="agent-key-description"
            placeholder={placeholder}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submit();
            }}
          />
        </FormGroup>
      </Col>
      <Col md="auto">
        <Button color="primary" disabled={loading} onClick={submit} data-testid={"agent-key-mint"}>
          <i className="fa fa-plus" /> Mint an agent key
        </Button>
      </Col>
    </Row>
  );
};

// The secret, exactly once: auth-repoflow stores its bcrypt hash and hands the
// plaintext back only in the create response, so closing this is the last
// anybody sees of it.
const SecretModal = ({ service, minted, onClose }) => {
  const connection = minted ? connectionOf(service, origin(), minted.keyId, minted.keySecret) : null;
  const wrap = { whiteSpace: "pre-wrap", wordBreak: "break-all" };

  return (
    <Modal isOpen={minted !== null} toggle={onClose} size="lg">
      <ModalHeader toggle={onClose}>Agent key {minted && minted.keyId}</ModalHeader>
      {connection && (
        <ModalBody>
          <Alert color="warning">
            Copy this now. The secret is shown once and cannot be recovered. If it is lost, revoke the key and
            mint another.
          </Alert>
          <p className="mb-1">Claude Code</p>
          <pre className="bg-light p-2" style={wrap} data-testid={"agent-key-claude-code"}>
            {connection.claudeCode}
          </pre>
          <p className="mb-1">MCP server</p>
          <pre className="bg-light p-2" style={wrap}>
            {connection.url}
          </pre>
          <p className="mb-1">Header</p>
          <pre className="bg-light p-2" style={wrap} data-testid={"agent-key-header"}>
            {connection.header}
          </pre>
          <p className="text-muted small mb-0">
            Any MCP client that can send a header works. Claude.ai and ChatGPT connectors cannot use a key yet.
          </p>
        </ModalBody>
      )}
      <ModalFooter>
        <Button color="primary" onClick={onClose}>
          I have copied it
        </Button>
      </ModalFooter>
    </Modal>
  );
};

const Listing = ({ queries, variables, refetch, update, empty, keysScreen }) => {
  const { data, loading, error } = useQuery(queries.agents, { variables, fetchPolicy: "cache-and-network" });
  const listing = data && data.viewer && data.viewer.account && data.viewer.account.keys.agents;

  if (error) return <Alert color="danger">{String(error)}</Alert>;
  if (!listing) return loading ? <Loading.Icon /> : null;

  const items = listing.items || [];
  return (
    <>
      {listing.complete === false && (
        <Alert color="warning">
          This organization has more keys than one look can read, so some agent keys may be missing here. The{" "}
          {keysScreen} screen lists them all.
        </Alert>
      )}
      {items.length === 0 ? (
        <p className="text-muted" data-testid={"agent-keys-empty"}>
          {empty}
        </p>
      ) : (
        <Table responsive data-testid={"agent-keys"}>
          <thead>
            <tr>
              <th>Key</th>
              <th>For</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((key) => (
              <tr key={key.keyId} data-testid={`agent-key-${key.keyId}`}>
                <td>
                  <code>{key.keyId}</code>
                </td>
                <td>{key.description || <span className="text-muted">—</span>}</td>
                <td>{key.createdAt}</td>
                <td className="text-nowrap text-right">
                  <Revoke keyId={key.keyId} mutation={queries.remove} refetch={refetch} update={update} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </>
  );
};

// Two presses, because a revoke cannot be undone and the agent holding the key
// stops working the moment it lands. Inline rather than a browser confirm().
const Revoke = ({ keyId, mutation, refetch, update }) => {
  const [armed, setArmed] = useState(false);
  const [revoke, { loading }] = useMutation(mutation, {
    ...refetch,
    ...(update ? { update: update({ keyId }) } : {}),
    onCompleted: () => Notify.success(`${keyId} revoked.`),
    onError: (error) => {
      setArmed(false);
      Notify.error(String(error));
    },
  });

  return armed ? (
    <>
      <Button color="danger" size="sm" disabled={loading} onClick={() => revoke({ variables: { keyId } })}>
        Revoke {keyId}
      </Button>{" "}
      <Button color="secondary" size="sm" onClick={() => setArmed(false)}>
        Keep
      </Button>
    </>
  ) : (
    <Button color="outline-danger" size="sm" onClick={() => setArmed(true)}>
      Revoke
    </Button>
  );
};
